import type { UpdateCompensationRequest } from '@api'
import {
  CreatorRestControllerService,
  SalaryRetention,
  TeamApiService,
} from '@api'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { handleError } from '@/utils/error'

interface UseSetCompensationParams {
  type: 'creator' | 'member'
}

export function useSetCompensation({ type }: UseSetCompensationParams) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const updateRevenueMutation = useMutation(
    ({ id, body }: { id: number; body: UpdateCompensationRequest }) => {
      if (type === 'member') {
        return TeamApiService.updateCompensation({
          id,
          requestBody: {
            fixedSalary: {
              amount: body.fixedSalary?.amount ?? 0,
              retention: body.fixedSalary?.retention ?? SalaryRetention.MONTHLY,
            },
            cutOfRevenuePercentage: body.cutOfRevenuePercentage ?? 0,
          },
        })
      }

      return CreatorRestControllerService.setCompensation({
        creatorId: id,
        requestBody: {
          fixedSalary: {
            amount: body.fixedSalary?.amount ?? 0,
            retention: body.fixedSalary?.retention ?? SalaryRetention.MONTHLY,
          },
          cutOfRevenuePercentage: body.cutOfRevenuePercentage ?? 0,
        },
      })
    },
    {
      onSuccess: () => {
        toast.success('Revenue successfully updated')
      },
      onError: (error) => {
        handleError(
          error,
          'Error occurred while updating revenue, please try again',
        )
      },
      onSettled: () => {
        queryClient.invalidateQueries('accounting_data')
        queryClient.invalidateQueries('accounting_snapshot')
      },
    },
  )

  const updateRevenue = (id: number, body: UpdateCompensationRequest) => {
    return updateRevenueMutation.mutateAsync({ id, body })
  }

  return {
    updateRevenue,
    isLoading: updateRevenueMutation.isLoading,
  }
}
